import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { RotateCcw, ArrowLeft } from "lucide-react";
import { GameModes } from "../../config";
import {
  useAviatrixLaunchMutation,
  useEuroVirtualLaunchUrlMutation,
  useExpanseGameLaunchUrlMutation,
  useFaziLaunchMutation,
  useGameLaunchUrlMutation,
  useLeprecoinsLaunchMutation,
  usePragmaticLaunchUrlMutation,
  useSaGameLaunchUrlMutation,
  useSmartsoftGameLaunchUrlMutation,
  useTadaGameLaunchUrlMutation,
  useVeligamesLaunchMutation,
  useVirtualGameLaunchUrlMutation,
} from "../../store/apis";
import Layout from "../../layout";
import { useSelector } from "react-redux";
import {
  selectIsAuthenticated,
  selectUser,
  setLoginModal,
} from "../../store/auth-slice";
import { getPlatformType, isMobileDevice } from "../../utils";
import logo from "../../images/logo.png";
import { LogoSpinnerLoader } from "../../Components/Loaders";
import { toastService } from "../notificationPage/ToastContainer";
import Slidersection from "../HomePage/Slider";

const GamePage = () => {
  const { gameAlias } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const provider = queryParams.get("provider");
  const mode = queryParams.get("mode") || GameModes.DEMO;
  const isVirtual =
    provider === "eurovirtual" || location.pathname.includes("virtual-game");
  const latestUser = useSelector(selectUser);
  const userRef = useRef(latestUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [gameUrl, setGameUrl] = useState("");
  const [iframeError, setIframeError] = useState(false);
  const [error, setError] = useState(null);
  const [generateGameUrl, { isLoading: isGettingGameLoading }] =
    useGameLaunchUrlMutation();
  const [generateVirtualGameUrl, { isLoading: isGettingVirtualGameLoading }] =
    useEuroVirtualLaunchUrlMutation();
  const [faziLaunch, { isLoading: isFaziLoading }] = useFaziLaunchMutation();
  const [aviatrixLaunch, { isLoading: isAviatrixLoading }] =
    useAviatrixLaunchMutation();
  const [expanseLaunch, { isLoading: isExpanseLoading }] =
    useExpanseGameLaunchUrlMutation();
  const [tadaLaunch, { isLoading: isTadaLoading }] =
    useTadaGameLaunchUrlMutation();
  const [leprecoinsLaunch, { isLoading: isLeprecoinsLoading }] =
    useLeprecoinsLaunchMutation();
  const [pragmaticLaunch, { isLoading: pragmaticLoading }] =
    usePragmaticLaunchUrlMutation();
  const [smartsoftLaunch, { isLoading: smartsoftLoading }] =
    useSmartsoftGameLaunchUrlMutation();
  const [sagamingLaunch, { isLoading: saLoading }] =
    useSaGameLaunchUrlMutation();
  const [veliLaunch, { isLoading: veliLoading }] = useVeligamesLaunchMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [gameInfo, setGameInfo] = useState(null);

  // Effect to update document title
  useEffect(() => {
    document.title = `${gameInfo?.name || gameAlias || "Game"} | Casino`;
  }, [gameInfo, gameAlias]);

  // Effect to log game URL for debugging
  useEffect(() => {
    console.log("Game URL:", gameUrl);
  }, [gameUrl]);
  // Sync the ref whenever Redux updates the user
  useEffect(() => {
    userRef.current = latestUser;
    // Optional: If you need to react to certain changes (e.g., logout), do it here
    // if (prevUser.current?.id !== latestUser.id) { ... }
  }, [latestUser]);
  // Check if user is logged in for real money mode
  useEffect(() => {
    if (mode === GameModes.REAL) {
      const currentUser = userRef.current;
      if (
        !currentUser ||
        (currentUser.balance != null && currentUser.balance < 0)
      ) {
        navigate("/casino");
        toastService.error("Error! Please login to proceed!!");
        setLoginModal(true);
      }
    }
  }, [mode, navigate]);
  useEffect(() => {
    if (mode === GameModes.REAL) {
      if (!isAuthenticated) {
        navigate("/casino");
        toastService.error("Error! Please login to proceed!!");
        setLoginModal(true);
      }
    }
  }, [isAuthenticated, mode, navigate, latestUser]);

  // Memoize the game launch function to prevent recreation on each render
  const launchGame = useCallback(async () => {
    if (!gameAlias || !provider) return;

    const currentUser = userRef.current;

    if (mode === GameModes.REAL) {
      if (
        !currentUser ||
        (currentUser.balance != null && currentUser.balance < 1)
      ) {
        if (
          currentUser &&
          currentUser.balance != null &&
          currentUser.balance < 1
        ) {
          toastService.error("Error! Low balance, top up to proceed!!");
          navigate("/casino");
          return;
        }
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      let response;

      if (isVirtual) {
        response = await generateVirtualGameUrl({
          username: currentUser?.username,
          player_id: currentUser?.id,
          demo: mode,
        }).unwrap();
        setGameUrl(response.data.data.url);
      } else if (provider === "fazi") {
        response = await faziLaunch({
          username: currentUser?.username,
          customer_id: currentUser?.id,
          gameName: gameAlias,
          mobile: isMobileDevice(),
          mode: mode,
        }).unwrap();
        setGameUrl(response.url);
      } else if (provider === "aviatrix") {
        response = await aviatrixLaunch({
          customer_id: currentUser?.id,
          mode: mode,
        }).unwrap();
        setGameUrl(response.url);
      } else if (provider === "leprecoins") {
        response = await leprecoinsLaunch({
          customer_id: currentUser?.id,
          gameId: gameAlias,
        }).unwrap();
        setGameUrl(response.url);
      } else if (provider === "veligames") {
        response = await veliLaunch({
          customer_id: currentUser?.id,
          gameId: gameAlias,
          mode,
          providerId: "veliplay",
        }).unwrap();
        setGameUrl(response.data.startGameUrl);
      } else if (provider === "velitest") {
        response = await veliLaunch({
          customer_id: currentUser?.id,
          gameId: gameAlias,
          mode,
          providerId: "acceptance-tests",
        }).unwrap();
        setGameUrl(response.data.startGameUrl);
      } else if (provider === "smartsoft") {
        response = await smartsoftLaunch({
          id: currentUser?.id,
          GameName: gameAlias,
          mode,
        }).unwrap();
        setGameUrl(response.url);
      } else if (provider === "sagaming") {
        response = await sagamingLaunch({
          username: currentUser?.username,
        }).unwrap();
        setGameUrl(response.launchUrl);
      } else if (provider === "expanse") {
        // Launch Aviatrix game
        response = await expanseLaunch({
          playerId: userRef.current?.username,
          platform: isMobileDevice() ? "mobile" : "desktop",
          platformType: getPlatformType(),
          mode: mode,
          gameCode: gameAlias,
        }).unwrap();
        setGameUrl(response.url);
        // } else if (provider === "aviator") {
        //   response = await aviator({
        //     userId: currentUser?.id,
        //   }).unwrap();
        //   setGameUrl(response.url);
      } else if (provider === "tada") {
        // Launch pragmatic game with subProvider handling
        response = await tadaLaunch({
          id: userRef.current?.id,
          gameId: gameAlias,
          // language: "en",
        }).unwrap();
        setGameUrl(response.Data);
      } else if (provider === "pragmaticplay" || provider === "pragmaticlive") {
        response = await pragmaticLaunch({
          externalPlayerId: currentUser?.id,
          symbol: gameAlias,
          mode: mode,
        }).unwrap();
        setGameUrl(response.gameURL);
      } else {
        response = await generateGameUrl({
          username: currentUser?.username,
          customer_id: currentUser?.id,
          provider: provider,
          gameAlias: gameAlias,
          mobile: isMobileDevice(),
          mode: mode,
        }).unwrap();
        setGameUrl(response.response.url);
      }

      if (response.status === 200 && response.response?.gameInfo) {
        setGameInfo(response.response.gameInfo);
      }
    } catch (error) {
      console.error("Game launch error:", error);
      setError(
        error.data?.message || "Failed to launch game. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    gameAlias,
    provider,
    mode,
    navigate,
    isVirtual,
    generateVirtualGameUrl,
    faziLaunch,
    aviatrixLaunch,
    leprecoinsLaunch,
    veliLaunch,
    smartsoftLaunch,
    sagamingLaunch,
    expanseLaunch,
    tadaLaunch,
    pragmaticLaunch,
    generateGameUrl,
  ]);
  // Launch game only once when component mounts
  useEffect(() => {
    launchGame();
  }, [launchGame]);
  const handleReload = () => {
    launchGame();
  };

  const handleReturnToGames = () => {
    navigate("/casino");
  };
  // Determine loading state by checking all possible loading states
  const isPageLoading =
    isLoading ||
    isGettingGameLoading ||
    isGettingVirtualGameLoading ||
    isFaziLoading ||
    isAviatrixLoading ||
    pragmaticLoading ||
    isExpanseLoading ||
    isLeprecoinsLoading ||
    isTadaLoading ||
    smartsoftLoading ||
    saLoading ||
    veliLoading;
  return (
    <Layout>
      <div className="flex-1 bg-[#F2F4F6] dark:bg-darkblue s15:pb-[100px]">
        {/* <div className="flex items-center justify-between p-3 bg-white dark:bg-darkblue shadow">
          <div className="flex items-center gap-3">
            <button
              onClick={handleReturnToGames}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full"
              aria-label="Return to games"
            >
              <ArrowLeft className="w-5 h-5 dark:text-white" />
            </button>
            <div>
              <h1 className="text-lg font-semibold dark:text-white">
                {gameInfo?.name || gameAlias || "Game"}
              </h1>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-300">
                {!isVirtual && (
                  <span>
                    ({mode === GameModes.DEMO ? "Demo" : "Real Money"})
                  </span>
                )}
                <span>{isVirtual ? "Euro Virtuals" : provider}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReload}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full"
              aria-label="Reload game"
            >
              <RotateCcw className="w-5 h-5 dark:text-white" />
            </button>
          </div>
        </div> */}

        <div className="h-full w-full md:px-5">
          {iframeError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 z-10">
              <div className="text-white text-center p-6 bg-gray-800 rounded-lg max-w-md">
                <p className="mb-4">Unable to load game directly.</p>
                <div className="flex gap-3 justify-center">
                  <a
                    href={gameUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Open in new window
                  </a>
                  <button
                    onClick={handleReturnToGames}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                  >
                    Back to Games
                  </button>
                </div>
              </div>
            </div>
          )}

          {error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500 p-4">
              <p className="text-center mb-3">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={handleReload}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={handleReturnToGames}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Back to Games
                </button>
              </div>
            </div>
          ) : isPageLoading ? (
            <div className="flex items-center justify-center w-full h-full min-h-[calc(100vh-200px)]">
              <LogoSpinnerLoader logoSrc={logo} />
            </div>
          ) : (
            <iframe
              src={gameUrl}
              className="w-full h-full min-h-[calc(100vh-200px)] border-none"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen *; picture-in-picture"
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation allow-top-navigation-by-user-activation allow-presentation"
              title={gameInfo?.name || gameAlias}
              allowFullScreen
              webkitAllowFullScreen="true"
              mozAllowFullScreen="true"
              msAllowFullScreen="true"
              onLoad={() => {
                console.log("Iframe loaded, URL:", gameUrl);
                setTimeout(() => setIsLoading(false), 1000); // Small delay to ensure content loads
              }}
              referrerpolicy="no-referrer-when-downgrade"
              onError={() => {
                console.error("Iframe loading error");
                setIframeError(true);
              }}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GamePage;
